<script setup lang="ts">
import type { APISearchLocationItem, YscDateRange } from '../../types'
import { onClickOutside, ref, useCalendarScreens, useNuxtApp, watch } from '#imports'

const { $i18n: { locale } } = useNuxtApp()

interface Props {
  destination?: string
  minDate?: string
  maxDate?: string
  results?: APISearchLocationItem[]
}

const props = withDefaults(defineProps<Props>(), {
  destination: undefined,
  minDate: undefined,
  maxDate: undefined,
  results: () => [],
  variant: null,
})

type SearchForm = {
  destination: APISearchLocationItem | null
  dates: YscDateRange
}

const emits = defineEmits<{
  'dates-change': [YscDateRange]
  'destination-change': [APISearchLocationItem]
  'submit': [{ form: SearchForm }]
  'destination-query-change': [string | null]
}>()

const form = ref<SearchForm>({
  destination: null,
  dates: {
    start: undefined,
    end: undefined,
  },
})

const onSubmit = () => {
  emits('submit', { form: form.value })
}

// Modal is use for mobile
const isSearchModal = ref(false)
// Popover is use for desktop
const isSearchPopover = ref(false)

const currentStep = ref(0)

const { screens } = useCalendarScreens()
const openSearchDestinations = () => {
  currentStep.value = 0
  openModalOrPopover()
}

const openSearchDates = () => {
  currentStep.value = 1
  openModalOrPopover()
}

const openModalOrPopover = () => {
  if (window.innerWidth < +screens.md.replace('px', '')) {
    isSearchModal.value = true
  }
  else {
    isSearchPopover.value = true
  }
}

const yscSearchRef = ref<HTMLFormElement>()
onClickOutside(yscSearchRef, () => {
  isSearchPopover.value = false
})

const localDestination = ref<string | undefined>(undefined)
const onDestinationChange = (destination: APISearchLocationItem) => {
  form.value.destination = destination
  localDestination.value = destination.label
  currentStep.value = 1
  emits('destination-change', destination)
}

watch(
  () => props.destination,
  (val) => {
    if (val === null || typeof val === 'string') {
      localDestination.value = val
      return
    }
  },
  {
    immediate: true,
  },
)

const onDatesChange = (dateRange?: YscDateRange) => {
  if (!dateRange) {
    return
  }
  form.value.dates = dateRange
  emits('dates-change', dateRange)
}

// FIXME: should be type of InstanceType<typeof YscRangeCalendar> | null
const rangeCalendarRef = ref<any>(null)
const onClearRange = () => {
  form.value.dates = { start: undefined, end: undefined }
  if (rangeCalendarRef.value) {
    rangeCalendarRef.value.clearRange()
  }
}
</script>

<template>
  <form
    ref="yscSearchRef"
    class="bg-white rounded pt-1 pb-2 md:pb-0 md:pt-0"
    autocomplete="off"
    @submit.prevent.stop="onSubmit"
  >
    <div class="relative md:flex md:grow items-center">
      <!-- Mobile -->
      <div class="md:hidden relative md:grow">
        <button
          class="w-full border-b border-gray-300 text-left py-2 px-4"
          @click.prevent.stop="openSearchDestinations"
        >
          <h3 class="font-semibold">
            {{ $t('data.search.destination.placeholder') }}
          </h3>
          <p
            v-if="!form.destination?.label"
            class="text-gray-400 my-1"
          >
            {{ $t('data.search.destination.label') }}
          </p>
          <p
            v-else
            class="flex my-1"
          >
            {{ form.destination?.label }}
          </p>
        </button>
      </div>

      <div class="md:hidden md:grow">
        <button
          class="w-full border-b border-gray-300 text-left py-2 px-4"
          @click.prevent.stop="openSearchDates"
        >
          <h3 class="font-semibold">
            {{ $t('data.search.dates.placeholder') }}
          </h3>
          <p
            v-if="!form.dates.start || !form.dates.end"
            class="text-gray-400 my-1"
          >
            {{ $t('data.search.dates.label') }}
          </p>
          <p
            v-else
            class="flex my-1"
          >
            {{ $d(new Date(form.dates.start), 'Y.numeric M.long D.numeric', locale) }}
            <YscIconsArrowRightLong class="h-6 w-6" />
            {{ $d(new Date(form.dates.end), 'Y.numeric M.long D.numeric', locale) }}
          </p>
        </button>
      </div>
      <!-- Fin Mobile -->

      <!-- Desktop -->
      <div class="hidden md:block grow">
        <YscSearchDestinationInput
          v-model="localDestination"
          :class="[{ 'bg-gray-50 rounded': currentStep !== 0 && isSearchPopover }]"
          @update:model-value="emits('destination-query-change', $event)"
          @focus="openSearchDestinations"
        />
      </div>

      <div class="hidden md:block md:grow">
        <button
          :class="[{ 'bg-gray-50': currentStep !== 1 && isSearchPopover }]"
          class="w-full text-left p-4"
          @click.prevent.stop="openSearchDates"
        >
          <h3 class="font-semibold">
            {{ $t('data.search.dates.placeholder') }}
          </h3>
          <p
            v-if="!form.dates.start || !form.dates.end"
            class="text-gray-400 my-1"
          >
            {{ $t('data.search.dates.label') }}
          </p>
          <p
            v-else
            class="flex text-nowrap my-1"
          >
            {{ $d(new Date(form.dates.start), 'Y.numeric M.long D.numeric', locale) }}
            <YscIconsArrowRightLong class="h-6 w-6 mx-1" />
            {{ $d(new Date(form.dates.end), 'Y.numeric M.long D.numeric', locale) }}
          </p>
        </button>
      </div>
      <!-- Fin Desktop -->

      <div class="mt-2 md:ml-4 md:mt-0 mx-3 py-1 md:shrink-0">
        <button
          class="btn btn-primary py-5 w-full rounded-sm"
        >
          {{ $t('commons.search') }}
        </button>
      </div>
      <div
        v-if="isSearchPopover && ((props.results && props.results.length) || currentStep !== 0)"
        class="absolute left-0 top-full z-50 bg-white w-full mt-2 rounded border border-gray-200 shadow-lg"
      >
        <YscSearchDestinationResults
          v-if="currentStep === 0"
          :local-destination="localDestination"
          :results="results"
          class="p-4"
          @destination-click="onDestinationChange"
        />
        <template v-else-if="currentStep === 1">
          <YscRangeCalendar
            ref="rangeCalendarRef"
            v-model="form.dates"
            :number-of-months="2"
            :min-date="minDate"
            :max-date="maxDate"
            class="border-b border-gray-300 p-4"
            @update:model-value="onDatesChange"
          />
          <div class="w-full flex justify-end pb-4 px-5">
            <button
              class="link text-gray-500 font-light mt-4"
              @click.prevent.stop="onClearRange"
            >
              <!-- TODO: fix -->
              {{ $t('data.search.dates.delete') }}
            </button>
          </div>
        </template>
      </div>
    </div>
    <Teleport to="body">
      <YscSearchModalFunnel
        v-if="isSearchModal"
        :results="results"
        :default-step="currentStep"
        :min-date="minDate"
        :max-date="maxDate"
        @close="isSearchModal = false"
        @destination-query-change="emits('destination-query-change', $event)"
        @destination-change="onDestinationChange"
        @dates-change="onDatesChange"
        @submit="onSubmit"
      />
    </Teleport>
  </form>
</template>
